<script>
export default {
  name: 'TestimonialsComponent',
  data() {
    return {
      currentIndex: 0,
      autoSlideInterval: null,
      isVisible: false,
      preloadedImages: new Set(),
      testimonials: [],
      isLoading: true
    };
  },
  mounted() {
    this.loadTestimonials();
  },
  beforeUnmount() {
    this.stopAutoSlide();
  },
  methods: {
    async loadTestimonials() {
      try {
        this.isLoading = true;
        
        // Try to load from NocoDB first
        try {
          const nocoService = (await import('@/services/nocodb.js')).default;
          const data = await nocoService.getTestimonials();
          
          if (data && data.list && data.list.length > 0) {
            this.testimonials = data.list.map(testimonial => ({
              ...testimonial,
              rating: testimonial.rating || 5,
              avatar: testimonial.avatar && testimonial.avatar.length > 0 
                ? `https://web-db.drods.ch/${testimonial.avatar[0].signedPath || testimonial.avatar[0].path}`
                : 'https://images.unsplash.com/photo-1754039985001-ccafee437736?w=150&h=150&fit=crop&crop=face&auto=format'
            }));
          }
        } catch (nocoError) {
          // Silently handle NocoDB connection issues
        }
        
        this.isLoading = false;
        if (this.testimonials.length > 0) {
          this.setupIntersectionObserver();
          this.preloadCurrentImage();
        }
      } catch (error) {
        this.isLoading = false;
      }
    },
    nextTestimonial() {
      this.currentIndex = (this.currentIndex + 1) % this.testimonials.length;
      this.resetAutoSlide();
    },
    previousTestimonial() {
      this.currentIndex = this.currentIndex === 0 
        ? this.testimonials.length - 1 
        : this.currentIndex - 1;
      this.resetAutoSlide();
    },
    goToTestimonial(index) {
      this.currentIndex = index;
      this.resetAutoSlide();
    },
    startAutoSlide() {
      // Longer interval on mobile to reduce battery drain
      const interval = window.innerWidth <= 768 ? 8000 : 6000;
      this.autoSlideInterval = setInterval(() => {
        this.nextTestimonial();
      }, interval);
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval);
      }
    },
    resetAutoSlide() {
      this.stopAutoSlide();
      this.startAutoSlide();
    },
    setupIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.isVisible) {
              this.isVisible = true;
              this.startAutoSlide();
              this.preloadAllImages();
            } else if (!entry.isIntersecting && this.isVisible) {
              this.stopAutoSlide();
            }
          });
        }, { threshold: 0.1 });
        
        observer.observe(this.$el);
      } else {
        // Fallback for browsers without IntersectionObserver
        this.isVisible = true;
        this.startAutoSlide();
        this.preloadAllImages();
      }
    },
    preloadCurrentImage() {
      const currentAvatar = this.testimonials[this.currentIndex].avatar;
      this.preloadImage(currentAvatar);
    },
    preloadAllImages() {
      // Preload remaining images with delay to avoid blocking
      this.testimonials.forEach((testimonial, index) => {
        if (index !== this.currentIndex) {
          setTimeout(() => {
            this.preloadImage(testimonial.avatar);
          }, index * 100);
        }
      });
    },
    preloadImage(src) {
      if (!this.preloadedImages.has(src)) {
        const img = new Image();
        img.loading = 'lazy';
        img.src = src;
        this.preloadedImages.add(src);
      }
    }
  }
}
</script>

<style scoped>
/* Testimonial slide transitions - optimized for mobile */
.testimonial-enter-active, .testimonial-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@media (max-width: 768px) {
  .testimonial-enter-active, .testimonial-leave-active {
    transition: all 0.3s ease-out;
  }
}

.testimonial-enter-from {
  opacity: 0;
  transform: translateX(60px) scale(0.95);
}

.testimonial-leave-to {
  opacity: 0;
  transform: translateX(-60px) scale(0.95);
}

.testimonial-enter-to, .testimonial-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}
</style>

<template>
  <div class="py-20 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-5">
      <div class="absolute inset-0" style="background-image: radial-gradient(circle at 20px 20px, #50327B 2px, transparent 2px); background-size: 80px 80px;"></div>
    </div>
    
    <div class="relative z-10 container mx-auto px-4">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-6" data-sal="slide-up" data-sal-delay="200">
          What Our Clients Say
        </h2>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto" data-sal="slide-up" data-sal-delay="400">
          Don't just take our word for it. Hear from the amazing clients we've had the pleasure to work with.
        </p>
      </div>
      
      <!-- Testimonials Carousel -->
      <div class="max-w-4xl mx-auto" data-sal="slide-up" data-sal-delay="600">
        <!-- Loading State -->
        <div v-if="isLoading || testimonials.length === 0" class="text-center py-16">
          <div class="w-16 h-16 border-4 border-[#d1d5db] border-t-primary rounded-full animate-spin mb-4 mx-auto"></div>
          <p class="text-gray-600">Loading testimonials...</p>
        </div>
        
        <div v-else class="relative">
          <!-- Main Testimonial Display -->
          <div class="relative min-h-[300px] md:min-h-[400px] flex items-center">
            <transition name="testimonial" mode="out-in">
              <div :key="currentIndex" class="w-full">
                <div class="bg-white rounded-3xl shadow-2xl p-6 md:p-8 lg:p-12 relative">
                  <!-- Testimonial Content -->
                  <div class="text-center">
                    <!-- Client Info -->
                    <div class="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
                      <!-- Avatar -->
                      <div class="relative">
                        <div class="w-24 h-24 rounded-full overflow-hidden border-4 border-accent shadow-lg">
                          <img 
                            :src="testimonials[currentIndex].avatar" 
                            :alt="testimonials[currentIndex].name"
                            class="w-full h-full object-cover"
                            loading="lazy"
                            @load="preloadImage(testimonials[currentIndex].avatar)"
                          >
                        </div>
                      </div>
                      
                      <!-- Client Details -->
                      <div class="text-center md:text-left">
                        <h4 class="font-bold text-gray-900 text-lg">{{ testimonials[currentIndex].name }}</h4>
                        <p class="text-primary font-medium">{{ testimonials[currentIndex].position }}<br />
                        <span class="text-gray-500 text-sm">{{ testimonials[currentIndex].company }}</span></p>
                      </div>
                    </div>
                    
                    <p class="text-base md:text-lg lg:text-xl text-gray-700 leading-relaxed mb-6 md:mb-8 italic">
                      "{{ testimonials[currentIndex].quote }}"
                    </p>
                    
                    <!-- Star Rating -->
                    <div class="flex justify-center mt-6 space-x-1">
                      <svg 
                        v-for="star in 5" 
                        :key="star"
                        :class="[
                          'w-5 h-5 transition-colors duration-200',
                          star <= testimonials[currentIndex].rating 
                            ? 'text-yellow-400 fill-current' 
                            : 'text-gray-300 fill-current'
                        ]"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
          
        </div>

        <!-- Dots Indicator with Navigation -->
        <div class="flex items-center justify-center gap-6 mt-8">
          <!-- Previous Arrow -->
          <button 
            @click="previousTestimonial"
            class="w-10 h-10 bg-white hover:bg-primary text-gray-600 hover:text-white !rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          >
            <svg class="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          
          <!-- Dots -->
          <div class="flex !space-x-2">
            <button
              v-for="(_, index) in testimonials"
              :key="index"
              @click="goToTestimonial(index)"
              :class="[
                'w-3 h-3 !rounded-full !border-0 !p-0 !transition-all !duration-300',
                index === currentIndex
                  ? '!bg-accent !scale-125'
                  : '!bg-dark/30 hover:!bg-dark/50'
              ]"
            ></button>
          </div>
          
          <!-- Next Arrow -->
          <button 
            @click="nextTestimonial"
            class="w-10 h-10 bg-white hover:bg-primary text-gray-600 hover:text-white !rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          >
            <svg class="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>